import { Phone, HeartHandshake, ShieldAlert, Sparkles, Users } from "lucide-react";
import { MaskedLine, FadeUp, Page, Meta } from "../components/Reveal";
import { useContent } from "../lib/ContentContext";

const GROUP_ICONS = [ShieldAlert, HeartHandshake, Sparkles, Users];

// Les liens tel: restent fixes (numéros officiels vérifiés) ; les textes affichés sont éditables.
const TELS = ["tel:988", "tel:+18662773553", "tel:811", "tel:+18003639010", "tel:+18889339007",
  "tel:+18665322822", "tel:+18002632266", "tel:+18006686868", "tel:+18885051010", "tel:211"];

const GROUP_CARDS = [[1, 2, 3], [4, 5, 6], [7, 8], [9, 10]];

export default function Ressources() {
  const c = useContent();
  let cardIndex = 0;

  const groups = GROUP_CARDS.map((cards, gi) => ({
    Icon: GROUP_ICONS[gi],
    title: c(`ressources.groupe.${gi + 1}.titre`),
    items: cards.map((n) => ({
      name: c(`ressources.carte.${n}.nom`),
      desc: c(`ressources.carte.${n}.desc`),
      phone: c(`ressources.carte.${n}.tel`),
      tel: TELS[n - 1],
    })),
  }));

  return (
    <Page testId="ressources-page">
      <section className="mx-auto max-w-7xl px-6 pt-36 md:px-12 md:pt-48">
        <FadeUp><Meta>{c("ressources.hero.meta")}</Meta></FadeUp>
        <h1 className="mt-6 max-w-4xl font-serif text-5xl font-light leading-[1.08] tracking-tight md:text-7xl">
          <MaskedLine delay={0.2}>{c("ressources.hero.l1")}</MaskedLine>
          <MaskedLine delay={0.35} className="italic text-clay">{c("ressources.hero.l2")}</MaskedLine>
        </h1>
        <FadeUp delay={0.5} className="mt-10 max-w-2xl">
          <p className="text-base leading-relaxed text-smoke md:text-lg">
            {c("ressources.hero.p")}
          </p>
        </FadeUp>
      </section>

      <FadeUp delay={0.1} className="mx-auto mt-14 max-w-7xl px-6 md:px-12">
        <div data-testid="urgent-banner" className="rounded-[1.75rem] bg-clay p-8 text-cream md:p-10">
          <div className="grid gap-6 md:grid-cols-2 md:gap-10">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.25em] text-cream/70">{c("ressources.urgence.911.titre")}</p>
              <a href="tel:911" data-testid="urgent-911"
                className="mt-3 inline-block font-serif text-4xl font-light tracking-tight md:text-5xl">
                911
              </a>
              <p className="mt-2 text-sm leading-relaxed text-cream/80">
                {c("ressources.urgence.911.texte")}
              </p>
            </div>
            <div className="md:border-l md:border-cream/20 md:pl-10">
              <p className="text-xs font-medium uppercase tracking-[0.25em] text-cream/70">{c("ressources.urgence.988.titre")}</p>
              <a href="tel:988" data-testid="urgent-988"
                className="mt-3 inline-block font-serif text-4xl font-light tracking-tight md:text-5xl">
                9-8-8
              </a>
              <p className="mt-2 text-sm leading-relaxed text-cream/80">
                {c("ressources.urgence.988.texte")}
              </p>
            </div>
          </div>
        </div>
      </FadeUp>

      <section className="mx-auto max-w-7xl px-6 py-20 md:px-12 md:py-28">
        {groups.map((g, gi) => (
          <div key={g.title} className={gi > 0 ? "mt-20" : ""}>
            <FadeUp>
              <div className="flex items-center gap-4">
                <g.Icon className={`h-6 w-6 ${gi % 2 === 1 ? "text-moss" : "text-clay"}`} strokeWidth={1.25} />
                <h2 className="font-serif text-2xl font-light tracking-tight md:text-3xl">{g.title}</h2>
              </div>
            </FadeUp>
            <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {g.items.map((r) => {
                cardIndex += 1;
                const n = cardIndex;
                return (
                  <FadeUp key={r.name} delay={0.05 * ((n - 1) % 3)} className="h-full">
                    <div data-testid={`resource-card-${n}`}
                      className="flex h-full flex-col rounded-[1.75rem] border border-linen bg-shell p-7 transition-colors duration-500 hover:bg-sand/30">
                      <h3 className="font-serif text-xl font-light leading-snug tracking-tight">{r.name}</h3>
                      <p className="mt-3 flex-1 text-sm leading-relaxed text-smoke">{r.desc}</p>
                      <a href={r.tel} data-testid={`resource-phone-${n}`}
                        className="group mt-6 inline-flex items-center gap-2.5 self-start rounded-full border border-linen px-5 py-2.5 text-sm font-medium text-charcoal transition-colors duration-300 hover:border-clay hover:text-clay">
                        <Phone className="h-4 w-4 text-clay" strokeWidth={1.5} />
                        {r.phone}
                      </a>
                    </div>
                  </FadeUp>
                );
              })}
            </div>
          </div>
        ))}

        <FadeUp delay={0.1}>
          <p data-testid="ressources-disclaimer" className="mt-20 rounded-[1.75rem] bg-sage/40 p-8 text-sm leading-relaxed text-smoke md:p-10 md:text-base">
            {c("ressources.disclaimer")}{" "}
            <a href="/clinique" className="text-clay underline-offset-4 hover:underline">Voir la page Clinique</a>
          </p>
        </FadeUp>
      </section>
    </Page>
  );
}
